/**
 * Watching "Start and test" / "Start all and run all checks" from the page (the sandbox's `starting`, one service at a
 * time): the wait must not outlive the services it waits for.
 * - The project stopped (Stop all: `flow:project-stopped`, or the runner says the service being started stopped,
 *   crashed or failed and nothing of the project runs): the start is over, nothing runs afterwards on its own.
 * - A service that never gets ready (a CLI such as miactl prints its help and exits; anything still not ready after
 *   START_STALL_MS): the page stops waiting for it, names it, and the checks of the services already ready run.
 * `startVerdict` is the pure decision; QualityPanel acts on it.
 */
import { isRunning, serviceState } from '../../services/SandboxService';

export { PROJECT_STOPPED_EVENT } from '../../services/SandboxService';
// Right after a start the runner may still say "stopped": a service is "down" only after this long.
export const START_GRACE_MS = 20 * 1000;
// Longer than this, a service still not ready is not waited for any more.
export const START_STALL_MS = 3 * 60 * 1000;

const DOWN = new Set(['stopped', 'failed', 'crashed']);

/**
 * Whether to stop waiting for the start `starting` (the overview's), from the runner's services (`known`: it could
 * say) at `now`: null (keep waiting), or `{ kind, ready, notStarted: [{ service, why }] }` where kind is `stopped` (the
 * project was stopped: nothing runs), `partial` (the checks of `ready` run without the others) or `stalled` (nothing
 * is ready: nothing runs).
 */
export function startVerdict(starting, { services = [], known = false } = {}, now = Date.now(), { stoppedByEvent = false } = {}) {
  if (!starting || starting.state !== 'starting') { return null; }
  const list = starting.services || [];
  const ready = list.filter((s) => s.state === 'ready').map((s) => s.service);
  const pending = list.filter((s) => s.state !== 'ready');
  if (stoppedByEvent) {
    return { kind: 'stopped', ready, notStarted: pending.map((s) => ({ service: s.service, why: 'stopped' })) };
  }
  if (!pending.length) { return null; }
  const elapsed = now - (starting.startedAt || now);
  const runnerOf = (name) => services.find((s) => s.serviceName === `services/${name}` || s.serviceName === name);
  const stateOf = (name) => (known ? serviceState(runnerOf(name)) : null);
  const current = pending.find((s) => s.state === 'starting') || null;
  const down = Boolean(current) && elapsed > START_GRACE_MS && DOWN.has(stateOf(current.service));
  const stalled = elapsed > START_STALL_MS;
  if (!down && !stalled) { return null; }
  const why = (s) => {
    if (s !== current) { return 'not started'; }
    if (down) { return stateOf(s.service) === 'crashed' ? 'it exited' : `it ${stateOf(s.service)}`; }
    return `not ready after ${Math.round(START_STALL_MS / 60000)} minutes`;
  };
  const notStarted = pending.map((s) => ({ service: s.service, why: why(s) }));
  // Nothing of the project runs any more: it was stopped (Stop all from another page, another tab, the CLI).
  if (down && known && !services.some(isRunning)) { return { kind: 'stopped', ready, notStarted }; }
  return { kind: ready.length ? 'partial' : 'stalled', ready, notStarted };
}
